import { Component, computed, effect, input, signal, ViewEncapsulation } from '@angular/core';

/**
 * Native image element: renders a `.ft-image-div` background image, preloading
 * `src` with `new Image()` and hiding the div until it loads (unless
 * `alwaysVisible`). Supports the profile-photo variant (`.ft-profile-pic`) and a
 * silhouette class shown while the photo is absent.
 */
@Component({
    selector: 'ft-image-ng',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./ft-image.component.scss'],
    templateUrl: './ft-image.component.html',
})
export class FtImageComponent {
    readonly src = input<string | null>();
    /** Mirrors the `ft-profile-photo` attribute directive (adds `.ft-profile-pic`). */
    readonly profilePhoto = input(false);
    /** Mirrors the bare `always-visible` attribute on `<ft-image>`. */
    readonly alwaysVisible = input(false);
    /**
     * Mirrors `ft-profile-photo`'s `silhouette-class`: the CSS class added to the
     * `.ft-image-div` when the photo does NOT exist (a default-avatar silhouette).
     * Removed once the image loads. (v1 profile.js `updateProfilePhoto`.)
     */
    readonly silhouetteClass = input<string | null>();

    protected readonly backgroundImage = signal<string | null>(null);
    /** undefined = unknown yet (hidden); true = loaded; false = failed/empty (hidden). */
    private readonly exists = signal<boolean | undefined>(undefined);

    protected readonly isVisible = computed(() => !!(this.alwaysVisible() || this.exists()));
    /** Apply the silhouette class only when the image is known-absent (exists === false). */
    protected readonly silhouetteClasses = computed(() =>
        this.exists() === false && this.silhouetteClass() ? this.silhouetteClass()! : '',
    );

    constructor() {
        effect(() => this.loadImage(this.src()));
    }

    private loadImage(val: string | null | undefined): void {
        this.backgroundImage.set(null);
        this.exists.set(undefined);
        if (!val) {
            this.exists.set(false);
            return;
        }
        let image: HTMLImageElement | null = new Image();
        image.onload = () => {
            this.backgroundImage.set(`url(${val})`);
            this.exists.set(true);
            image = null;
        };
        image.onerror = () => {
            this.backgroundImage.set(null);
            this.exists.set(false);
            image = null;
        };
        image.src = val;
    }
}
